// Lion Auto match-result helpers shared by the page and its components: how
// a listing, a price, an exclusion reason, a failed call and a result's
// status codes read in plain words. Display only — nothing here computes or
// suggests a price. The table itself is LaListingsTable.
import { detailLines, retryAfterSec } from '../../utils/lionAutoErrors';
import { allOutliersText, outliersDroppedOf } from '../../utils/lionAutoOutliers';
import { zoneRefText } from '../../utils/lionAutoListings';

// Moved to utils (a util reads them too); still exported here for the callers
export { detailLines, retryAfterSec };

// Why a comp was excluded, or (QTY, ROW, NOTES, GROUP, MANUAL) why one of
// our listings in the group's sections is not a member
export const REASON_LABELS = {
  NO_PRICE: 'No price',
  SECTION: 'Other section',
  // under the Qty filter's min or (1 only) over its max
  QTY: 'Outside qty filter',
  ROW: 'Row not picked',
  SPLIT: "Splits don't fit",
  NOTES: 'Public notes',
  OUTLIER: 'Low outlier',
  // A listing is in one group per event: the group that first held it keeps
  // it (the server's detail names that group)
  GROUP: 'In another group',
  // Excluded by hand in the table: one of ours (the group's excludeIds), or a
  // market listing taken out of the comps (Not a comp: its excludeCompIds)
  MANUAL: 'Excluded by you',
};

// "$123.45", or a dash when there is no usable price
export const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? `$${n.toFixed(2)}` : '—';
};

export const sectionText = (l) => l?.displayName || l?.section || '-';

// "Sec 112 Row A" — how a pricer names a listing out loud
export const listingLabel = (l) => (l ? `Sec ${sectionText(l)} Row ${l.row || '-'}` : '');

// Plain-words reading of a failed match call
export function describeMatchError(err) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  if (status === 403) return { text: 'You do not have access to this event.', retryable: false };
  if (status === 404) {
    // A bare { error: 'Not found' } (no code) is the server's unknown-route
    // answer: the Lion Auto backend is not deployed there yet
    let text = 'Lion Auto is not available on this server yet.';
    if (data.code === 'PRICING_NOT_FOUND') text = 'Skybox has no pricing for this event.';
    else if (data.code === 'NOT_FOUND') text = 'This group no longer exists.';
    else if (data.code) text = data.error || 'Not found.';
    return { text, retryable: false };
  }
  if (status === 400) {
    return { text: data.error || 'These criteria were not accepted.', details: detailLines(data.details), retryable: false };
  }
  if (status === 429) return { text: `Too many match requests — try again in ${retryAfterSec(err)} s.`, retryable: true };
  if (status === 503 || !status || err?.code === 'ECONNABORTED') {
    return { text: 'Skybox is temporarily unavailable — this usually clears in a moment.', retryable: true };
  }
  return { text: data.error || 'Matching failed.', retryable: true };
}

// "42 s ago" / "5 min ago" / "2 h ago"
export function ageText(ms) {
  if (!Number.isFinite(ms) || ms < 0) return null;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  return `${Math.round(m / 60)} h ago`;
}

// Status codes → banners in plain words. warn = results usable with a caveat
// or a group not built yet.
export function statusBanners(result) {
  const out = [];
  const codes = new Set(result?.status || []);
  if (codes.has('NO_SECTIONS')) {
    // Only a saved group can have no sections (a draft is never matched
    // without one), so the way out goes through Edit
    out.push({ code: 'NO_SECTIONS', kind: 'warn', text: 'This group has no sections yet. Edit it and click sections on the map to build it.' });
  }
  if (codes.has('NO_MEMBERS')) {
    // Ours in the sections that the group's Qty / rows / public-notes
    // filters kept out, that another group holds, or that the pricer
    // excluded by hand (a narrow group on the sections of an older, broad
    // one says why it is empty; an older server sends no
    // membersExcludedByGroup / membersExcludedByManual)
    const belowQty = Number(result?.totals?.membersBelowQty) || 0;
    const byRows = Number(result?.totals?.membersExcludedByRows) || 0;
    const byNotes = Number(result?.totals?.membersExcludedByNotes) || 0;
    const byGroup = Number(result?.totals?.membersExcludedByGroup) || 0;
    const byManual = Number(result?.totals?.membersExcludedByManual) || 0;
    const kept = [
      belowQty ? `${belowQty} outside the Qty filter` : null,
      byRows ? `${byRows} left out by the rows filter` : null,
      byNotes ? `${byNotes} left out by the public notes filter` : null,
      byGroup ? `${byGroup} held by ${byGroup === 1 ? 'another group' : 'other groups'}` : null,
      byManual ? `${byManual} excluded by you` : null,
    ].filter(Boolean);
    out.push({
      code: 'NO_MEMBERS', kind: 'warn',
      text: `You have no listings in these sections${kept.length ? ` that this group can take (${kept.join(', ')})` : ''}; the market is shown so you can still see it.`,
    });
  }
  // With no sections, "no comps" is a consequence, not a separate problem.
  // The zone fallback searched the sections' zone too: it says so
  if (codes.has('NO_COMPS') && !codes.has('NO_SECTIONS')) {
    const text = result?.resolved?.zoneSearched === true
      ? `No market listings in these sections or ${zoneRefText(result.resolved, 'their')} match.`
      : 'No market listings in these sections match.';
    out.push({ code: 'NO_COMPS', kind: 'warn', text });
  }
  // No comp in the sections: the zone fallback priced the group off their zone
  if (codes.has('ZONE_COMPS')) {
    out.push({ code: 'ZONE_COMPS', kind: 'warn', text: `No comp in the group's sections: priced off ${zoneRefText(result?.resolved)}` });
  }
  if (codes.has('ALL_COMPS_OUTLIERS')) {
    out.push({ code: 'ALL_COMPS_OUTLIERS', kind: 'warn', text: allOutliersText(outliersDroppedOf(result)) });
  }
  return out;
}
